import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import { processImage } from 'js/lib/imgix';

import type { ProductType } from '@coursera/event-pulse-types';

import { PRODUCT_CARD_IMAGE_DEFAULT_VALUES } from 'bundles/collections-carousel/constants';
import type { IntlShim } from 'bundles/page/lib/useIntl';
import type { ProductCardImageFitProps } from 'bundles/product-card/components/legacy/ProductCardImage';
import type { ProductCardPartner } from 'bundles/product-card/components/types/ProductCardV2';
import { PRODUCT_CARD_V2_IMAGE_DEFAULT_VALUES } from 'bundles/product-card/constants';
import { LEARNING_PRODUCTS, getTranslatedProductName } from 'bundles/search-common/constants';
import { AUTOCOMPLETE_PHOTO_SIZE, CourseIcon } from 'bundles/search-common/constants/autocomplete';
import { saveRecentlyViewed } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/product-card';

// Valid enum values: BEGINNER, INTERMEDIATE, ADVANCED, MIXED
export const convertDifficultyToEnum = (difficulty: string | null | undefined): DifficultyLevel | null => {
  if (!difficulty) return null;
  const upperCase = difficulty.toUpperCase();
  if (upperCase === 'BEGINNER' || upperCase === 'INTERMEDIATE' || upperCase === 'ADVANCED' || upperCase === 'MIXED') {
    return upperCase as DifficultyLevel;
  }
  return null;
};

// The subset of saveRecentlyViewed's fields that a product card's own identity props
// (id/name/slug/productType/isPathwayProduct/href) can't already supply — namely the
// card's own image and partner-logo strings, and the raw (untranslated) difficulty enum.
export type AutocompleteProductCardData = {
  cardImageUrl?: string | null;
  partnerName?: string;
  partnerLogo?: string;
  difficulty?: DifficultyLevel | null;
  careerGrowthInPercentage?: number | null;
  medianEntryLevelSalary?: number | null;
  jobOpeningsInUs?: number | null;
};

export type ProductCardIdentity = {
  id?: string;
  name: string;
  slug?: string;
  /** The URL the card navigates to — also the path recorded to Recently Viewed. */
  path: string;
  productType?: string | null;
  isPathwayProduct?: boolean;
};

/** Records a product card click to Recently Viewed (search autocomplete). Call only on an
 * actual navigation to the product's own page — not for non-navigating clicks. */
export const saveProductCardRecentlyViewed = (
  product: ProductCardIdentity,
  autocompleteProductCardData: AutocompleteProductCardData
) => {
  // Since #67245 every product-card surface writes Recently Viewed, not just search.
  // Don't persist an entry missing the fields the zero-state autocomplete renders/navigates
  // with — a malformed entry would otherwise crash the dropdown on the next expand.
  if (!product?.name || !product?.path) {
    return;
  }
  const {
    cardImageUrl,
    partnerName,
    partnerLogo,
    difficulty,
    careerGrowthInPercentage,
    medianEntryLevelSalary,
    jobOpeningsInUs,
  } = autocompleteProductCardData;
  saveRecentlyViewed({
    id: product.id,
    name: product.name,
    slug: product.slug,
    path: product.path,
    imageIconWithSize: cardImageUrl ? { imageUrl: cardImageUrl, size: AUTOCOMPLETE_PHOTO_SIZE } : CourseIcon,
    cardImageUrl,
    partnerName,
    partnerLogo,
    difficulty,
    productType: product.productType ?? null,
    isPathwayProduct: product.isPathwayProduct ?? false,
    careerGrowthInPercentage,
    medianEntryLevelSalary,
    jobOpeningsInUs,
  });
};

export const getPartnerNamesAndLogos = (
  partners: { id?: string; logo?: string; name?: string }[],
  cobrandingEnabled?: boolean,
  partnerLogos?: string[]
): ProductCardPartner[] => {
  const newPartnersArray = partners.map((partner, index) => {
    const logoUrl = processImage(partnerLogos?.[index] ?? '', { width: 24, height: 24, dpr: 3 });
    return { id: partner.id || '', name: partner.name || '', logoUrl };
  });
  return cobrandingEnabled ? newPartnersArray : newPartnersArray.slice(0, 1);
};

/* Set string to all lowercase and capitalize each word in string */
export const capitalizeEachWordInEntityType = (
  entityType?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS]
) => {
  const modifiedEntityType = entityType
    ?.toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(' ');

  return modifiedEntityType;
};

export const capitalizeAndTranslateEachWordInEntityType = (
  entityType?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS]
) => getTranslatedProductName(capitalizeEachWordInEntityType(entityType));

export const extractEventId = (id: string | undefined | null, type?: ProductType, isSearch?: boolean) => {
  if (typeof id !== 'string') {
    return 'undefined';
  }
  if (type === 'clip' && isSearch) {
    // Clips in search are split with -
    const lastDash = id?.lastIndexOf('-');
    if (lastDash !== -1) {
      return id?.substring(lastDash + 1);
    }
  } else if (type === 'clip') {
    // clips id in collections are seperated by ~
    const lastTilde = id?.lastIndexOf('~');
    if (lastTilde !== -1) {
      return id?.substring(lastTilde + 1);
    }
  }
  const splitId = id.split('~');
  if (splitId.length > 1) {
    return splitId[1];
  }
  return id;
};

export const formatProductTypeWithCourseCount = (
  intl: IntlShim,
  productVariant: string,
  courseCount: number
): string => {
  const normalizedProductVariant =
    productVariant === 'PROFESSIONAL_CERTIFICATE_S12N' ? 'PROFESSIONAL CERTIFICATE' : 'SPECIALIZATION';
  return intl.formatMessage(
    _t('{translatedProductName} ({courseCount} {courseCount, plural, =1 {course} other {courses}})'),
    {
      translatedProductName: capitalizeAndTranslateEachWordInEntityType(normalizedProductVariant) || 'Unknown',
      courseCount,
    }
  );
};

export const eventingV3ProductType = (type?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS]) => {
  switch (type) {
    case LEARNING_PRODUCTS.Course:
    case LEARNING_PRODUCTS.GuidedProject:
    case LEARNING_PRODUCTS.Project:
      return 'course';
    case LEARNING_PRODUCTS.Video:
    case LEARNING_PRODUCTS.Lesson:
      return 'clip';
    case LEARNING_PRODUCTS.Specialization:
    case LEARNING_PRODUCTS.ProfessionalCertificate:
      return 's12n';
    case LEARNING_PRODUCTS.Degree:
    case LEARNING_PRODUCTS.PostgraduateDiploma:
      return 'degree';
    case LEARNING_PRODUCTS.UniversityCertificate:
    case LEARNING_PRODUCTS.Mastertrack:
    case LEARNING_PRODUCTS.GraduateCertificate:
      return 'premium_certificate';
    case LEARNING_PRODUCTS.LearningPath:
      return 'learning_path';
    default:
      return undefined; // or some other default value as needed
  }
};

export type PartnerToImageFitMap = Record<
  string,
  Record<string, ProductCardImageFitProps> & { default: ProductCardImageFitProps }
>;
// Shared image fit presets reused across multiple partner images.
const LEGACY_IMAGE_FIT: ProductCardImageFitProps = { ...PRODUCT_CARD_IMAGE_DEFAULT_VALUES };
const FOCALPOINT_IMAGE_FIT: ProductCardImageFitProps = {
  ...PRODUCT_CARD_V2_IMAGE_DEFAULT_VALUES,
  crop: 'focalpoint',
  fpY: 0.48,
};

const PARTNER_IMAGE_FIT_MAP: PartnerToImageFitMap = {
  Google: {
    default: { ...PRODUCT_CARD_V2_IMAGE_DEFAULT_VALUES, crop: 'faces' },
    'GwG-DA-Cert-Image.png': LEGACY_IMAGE_FIT,
    '1200x600_certsResize_3-1-.png': LEGACY_IMAGE_FIT,
    '1200x600_certsResize_1.png': LEGACY_IMAGE_FIT,
    '1200x600_certsResize_1-1-.png': LEGACY_IMAGE_FIT,
    'Andrew-WA-Thompson-699-1-.jpg': LEGACY_IMAGE_FIT,
    'GwG-PM-Cert-Image.png': { ...PRODUCT_CARD_V2_IMAGE_DEFAULT_VALUES, crop: 'entropy' },
    '082421_Karrim0066-1.png': LEGACY_IMAGE_FIT,
    '3_Kenefra.png': LEGACY_IMAGE_FIT,
    'AI-Professional-Cert_Hero_AMMAAR.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-ai
    'SME_AI-Professional-Cert_AMMAAR-RESHI_APP-Deployment.png': {
      ...PRODUCT_CARD_V2_IMAGE_DEFAULT_VALUES,
      crop: 'focalpoint',
      fpY: 0.44,
    }, // learn/google-ai-for-app-deployment
    'PME.png': FOCALPOINT_IMAGE_FIT, // specializations/google-people-management-essentials
    'Cybersecurity.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-cybersecurity
    'AIE.png': FOCALPOINT_IMAGE_FIT, // specializations/ai-essentials-google
    'UX-Design.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-ux-design
    'DME.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-digital-marketing-ecommerce
    'GCC-Coursera-thumbnail-agile-essentials-1-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-agile-essentials
    'GCC-Coursera-thumbnail-Accelerate-your-job-search-uncover-your-skills-tony-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-accelerate-your-job-search-with-ai
    'GCC-Coursera-thumbnail-DA-foundations-tony-cert-level.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-data-analytics
    'GCC-Coursera-thumbnail-ADA-foundations-of-data-science-casie-cert-level.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-advanced-data-analytics
    'GCC-Coursera-thumbnail-BI-process-data-sally-cert-level.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-business-intelligence
    'GCC-Coursera-thumbnail-IT-tech-support-fundamentals-kevin-cert-level.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-it-support
    'GCC-Coursera-thumbnail-ITwithPython-crash-course-python-christine-cert-level.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-it-automation
    'GCC-Coursera-thumbnail-PM-foundations-emilio-cert-level.png': FOCALPOINT_IMAGE_FIT, // professional-certificates/google-project-management
    'GCC-Coursera-thumbnail-DAwithPython-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-data-analysis-with-python
    'GCC-Coursera-thumbnail-DataDriven-Decision-Making-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-data-driven-decision-making
    'GCC-Coursera-thumbnail-Digital-customer-engagement-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-digital-customer-engagement
    'GCC-Coursera-thumbnail-Network-security-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-network-security
    'GCC-Coursera-thumbnail-Stakeholder-management-cert-level.png': FOCALPOINT_IMAGE_FIT, // specializations/google-stakeholder-management
  },
};

/**
 *
 * For some cases, for now, we need to modify the image props based on the partners and image name.
 *
 * This functionality is considered a "quick fix" for a longer standing issue that the Authoring Team will
 * address in Q3.
 */
export const getImagePropOverrides = (
  partners: ProductCardPartner[],
  imageUrl: string,
  imageFitMap = PARTNER_IMAGE_FIT_MAP
): ProductCardImageFitProps | undefined => {
  const relevantPartnerName = partners?.find((partner) => Object.keys(imageFitMap).includes(partner.name))?.name;
  if (!relevantPartnerName) return undefined;
  const fileName = imageUrl.split('/').pop() ?? '';
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return imageFitMap[relevantPartnerName][fileName] ?? imageFitMap[relevantPartnerName].default;
};
